import Navbar from '../home/components/Navbar';
import Footer from '../home/components/Footer';
import PricingTiers from './components/PricingTiers';
import SeasonalServices from './components/SeasonalServices';

const siteUrl = import.meta.env.VITE_SITE_URL ?? 'https://hornpmc.ca';

const pageSeo = {
  title: 'Property Maintenance Burlington ON – Lawn & Snow Care | Horn PMC',
  description:
    'Reliable property maintenance in Burlington, Oakville & Hamilton. Lawn mowing, hedge trimming, spring/fall cleanups, and snow removal. Seasonal packages available. Free estimates — 905-869-4676.',
  canonical: `${siteUrl}/property-maintenance`,
  keywords: 'property maintenance Burlington, lawn care Burlington, snow removal Burlington, landscaping Oakville Hamilton',
};

const serviceSchema = {
  '@context': 'https://schema.org',
  '@type': 'Service',
  name: 'Property Maintenance Services',
  description:
    'Comprehensive seasonal property maintenance including lawn mowing, hedge trimming, spring & fall cleanups, and snow removal for Burlington, Oakville, and Hamilton homeowners.',
  provider: {
    '@type': 'LocalBusiness',
    name: 'Horn Property Maintenance and Construction',
    telephone: '+19058694676',
    url: siteUrl,
    address: {
      '@type': 'PostalAddress',
      addressLocality: 'Burlington',
      addressRegion: 'ON',
      addressCountry: 'CA',
    },
  },
  areaServed: ['Burlington, ON', 'Oakville, ON', 'Hamilton, ON', 'Mississauga, ON', 'Milton, ON'],
  serviceType: [
    'Lawn Mowing',
    'Hedge Trimming',
    'Spring Cleanup',
    'Fall Cleanup',
    'Snow Removal',
    'Property Maintenance',
  ],
  url: pageSeo.canonical,
  offers: {
    '@type': 'Offer',
    availability: 'https://schema.org/InStock',
    priceSpecification: {
      '@type': 'PriceSpecification',
      priceCurrency: 'CAD',
      description: 'Seasonal maintenance packages — free estimates available',
    },
  },
};

const webPageSchema = {
  '@context': 'https://schema.org',
  '@type': 'WebPage',
  name: pageSeo.title,
  description: pageSeo.description,
  url: pageSeo.canonical,
  isPartOf: {
    '@type': 'WebSite',
    name: 'Horn PMC',
    url: siteUrl,
  },
  breadcrumb: {
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: siteUrl },
      { '@type': 'ListItem', position: 2, name: 'Property Maintenance', item: pageSeo.canonical },
    ],
  },
};

const processSteps = [
  {
    step: '01',
    title: 'Get a Free Quote',
    desc: 'Call us or fill out the form online. We\'ll discuss your property size, what you need, and recommend the right package.',
  },
  {
    step: '02',
    title: 'We Schedule Your First Visit',
    desc: 'Once you\'re signed up, we lock in a recurring schedule that works for you and show up rain or shine.',
  },
  {
    step: '03',
    title: 'Sit Back & Enjoy',
    desc: 'Your lawn and property stay in top shape all season. No reminders needed — we just take care of it.',
  },
];

const stats = [
  { value: '200+', label: 'Properties Maintained' },
  { value: '5★', label: 'Average Rating' },
  { value: '8+', label: 'Years in Burlington' },
  { value: '100%', label: 'Satisfaction Guarantee' },
];

export default function PropertyMaintenance() {
  return (
    <>
      <title>{pageSeo.title}</title>
      <meta name="description" content={pageSeo.description} />
      <meta name="keywords" content={pageSeo.keywords} />
      <link rel="canonical" href={pageSeo.canonical} />
      <meta property="og:title" content={pageSeo.title} />
      <meta property="og:description" content={pageSeo.description} />
      <meta property="og:url" content={pageSeo.canonical} />
      <meta property="og:type" content="website" />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={pageSeo.title} />
      <meta name="twitter:description" content={pageSeo.description} />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(serviceSchema) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(webPageSchema) }}
      />
      <main style={{ fontFamily: 'Inter, sans-serif' }}>
        <Navbar />

        {/* ── Hero ── */}
        <section className="relative w-full h-[520px] flex items-end pb-16">
          <div className="absolute inset-0">
            <img
              src="https://public.readdy.ai/ai/img_res/2e956e6d585b1fc987886c0c842933c3.jpg"
              alt="Property Maintenance Services Burlington Ontario"
              className="w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/75 via-black/30 to-black/15"></div>
          </div>

          {/* Breadcrumb */}
          <div className="absolute top-28 left-6 lg:left-16 flex items-center gap-2 text-white/70 text-xs">
            <a href="/" className="hover:text-white transition-colors">Home</a>
            <i className="ri-arrow-right-s-line"></i>
            <span className="text-white">Property Maintenance</span>
          </div>

          <div className="relative z-10 w-full px-6 lg:px-16">
            <p className="text-[#C8A97E] text-xs font-bold tracking-[0.4em] uppercase mb-3">OUR SERVICES</p>
            <h1
              className="text-white text-5xl lg:text-6xl font-bold leading-tight mb-4"
              style={{ fontFamily: 'Playfair Display, serif' }}
            >
              Property<br />Maintenance
            </h1>
            <p className="text-white/75 text-base max-w-xl">
              Consistent, reliable property care for Burlington, Oakville, and Hamilton homeowners — all season long.
            </p>
          </div>
        </section>

        {/* ── Stats bar ── */}
        <section className="bg-[#2B5741] py-8 px-6">
          <div className="max-w-4xl mx-auto grid grid-cols-2 md:grid-cols-4 gap-6">
            {stats.map((s) => (
              <div key={s.label} className="text-center">
                <p
                  className="text-white text-3xl font-bold"
                  style={{ fontFamily: 'Playfair Display, serif' }}
                >
                  {s.value}
                </p>
                <p className="text-white/60 text-xs uppercase tracking-wider mt-1">{s.label}</p>
              </div>
            ))}
          </div>
        </section>

        {/* ── Intro ── */}
        <section className="bg-white py-20 px-6">
          <div className="max-w-5xl mx-auto flex flex-col lg:flex-row gap-14 items-center">
            <div className="flex-1">
              <p className="text-[#C8A97E] text-xs font-bold tracking-[0.4em] uppercase mb-4">WHO WE ARE</p>
              <h2
                className="text-[#2B5741] text-3xl font-bold mb-5"
                style={{ fontFamily: 'Playfair Display, serif' }}
              >
                Burlington&apos;s Property Care Experts
              </h2>
              <p className="text-gray-600 text-sm leading-relaxed mb-4">
                At Horn PMC, we take the hassle out of property upkeep. Our maintenance packages are designed
                for busy homeowners who want a beautiful, well-kept property without lifting a finger.
              </p>
              <p className="text-gray-600 text-sm leading-relaxed mb-6">
                From spring cleanups to weekly mowing, hedge trimming, fall leaf removal, and winter snow
                clearing — we handle it all on a consistent schedule so nothing slips through the cracks.
              </p>
              <div className="flex flex-col sm:flex-row gap-3">
                <a
                  href="#packages"
                  className="bg-[#2B5741] text-white px-7 py-3 text-xs font-bold uppercase tracking-[0.2em] hover:bg-[#3A7060] transition-colors whitespace-nowrap rounded-md text-center"
                >
                  SEE PACKAGES
                </a>
                <a
                  href="tel:9058694676"
                  className="flex items-center justify-center gap-2 border-2 border-[#2B5741] text-[#2B5741] px-7 py-3 text-xs font-bold uppercase tracking-[0.2em] hover:bg-[#2B5741] hover:text-white transition-colors whitespace-nowrap rounded-md"
                >
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-phone-fill"></i>
                  </div>
                  905-869-4676
                </a>
              </div>
            </div>
            <div className="lg:w-96 w-full h-72 rounded-xl overflow-hidden flex-shrink-0">
              <img
                src="https://readdy.ai/api/search-image?query=Professional%20landscaping%20crew%20working%20in%20neat%20suburban%20residential%20yard%20trimming%20hedges%20and%20maintaining%20garden%20beds%2C%20Burlington%20Ontario%20Canada%20property%20maintenance%20team%2C%20natural%20outdoor%20daylight%2C%20clean%20tidy%20property%20exterior%2C%20two%20workers%20visible%20with%20equipment&width=700&height=520&seq=pmintro1&orientation=landscape"
                alt="Horn PMC property maintenance crew Burlington"
                className="w-full h-full object-cover object-top"
              />
            </div>
          </div>
        </section>

        {/* ── Pricing Tiers ── */}
        <PricingTiers />

        {/* ── Seasonal Services ── */}
        <SeasonalServices />

        {/* ── How It Works ── */}
        <section className="bg-white py-20 px-6">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <p className="text-[#C8A97E] text-xs font-bold tracking-[0.4em] uppercase mb-4">SIMPLE PROCESS</p>
              <h2
                className="text-[#2B5741] text-4xl font-bold"
                style={{ fontFamily: 'Playfair Display, serif' }}
              >
                How It Works
              </h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {processSteps.map((step, i) => (
                <div key={step.step} className="flex flex-col items-center text-center">
                  <div className="w-14 h-14 flex items-center justify-center bg-[#2B5741] rounded-full mb-5 flex-shrink-0">
                    <span
                      className="text-[#C8A97E] text-sm font-bold"
                      style={{ fontFamily: 'Playfair Display, serif' }}
                    >
                      {step.step}
                    </span>
                  </div>
                  <div className="hidden md:block" />
                  <h3
                    className="text-gray-800 font-bold text-base mb-3"
                    style={{ fontFamily: 'Playfair Display, serif' }}
                  >
                    {step.title}
                  </h3>
                  <p className="text-gray-500 text-sm leading-relaxed">{step.desc}</p>
                  {i < processSteps.length - 1 && (
                    <div className="md:hidden w-px h-8 bg-gray-200 mt-6"></div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ── CTA ── */}
        <section className="bg-[#2B5741] py-16 px-6">
          <div className="max-w-3xl mx-auto text-center">
            <p className="text-[#C8A97E] text-xs font-bold tracking-[0.4em] uppercase mb-4">NOW BOOKING</p>
            <h2
              className="text-white text-4xl font-bold mb-5"
              style={{ fontFamily: 'Playfair Display, serif' }}
            >
              Ready for a Stress-Free Property?
            </h2>
            <p className="text-white/70 text-sm mb-8 max-w-lg mx-auto">
              Spots for our 2026 season are filling up fast. Lock in your package today and get 10% off when you book before April 15th.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <a
                href="/#contact"
                className="bg-[#C8A97E] text-white px-10 py-3.5 text-xs font-bold uppercase tracking-[0.2em] hover:bg-[#b8976a] transition-colors whitespace-nowrap rounded-md"
              >
                GET A FREE QUOTE
              </a>
              <a
                href="tel:9058694676"
                className="flex items-center gap-2 border-2 border-white text-white px-10 py-3.5 text-xs font-bold uppercase tracking-[0.2em] hover:bg-white hover:text-[#2B5741] transition-colors whitespace-nowrap rounded-md"
              >
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-phone-fill"></i>
                </div>
                905-869-4676
              </a>
            </div>
          </div>
        </section>

        <Footer />
      </main>
    </>
  );
}